import { IChoiceGroupOption } from '@fluentui/react';
import { IVerticalStackedChartProps, IVSChartDataPoint, VerticalStackedBarChart } from '@fluentui/react-charting';
import AppContext from 'App/AppContext';
import { IPActionButton, IPCheckbox, IPChoiceGroup, IPSpinner, IPText } from 'baseComponents';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import TenantPicker, { PickerTenant, TenantPickerMode } from 'components/Pickers/TenantPicker';
import { globalStackTokensGapMedium, viewIcon } from 'globalStyles';
import RiskOwnerDTO from 'models/dto/riskOwnerDTO';
import { isPartnerWidgetType, WidgetSizes, WidgetTenantConfig, WidgetWidth } from 'models/widget';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetWidgetDataTenantRiskOwner } from 'services/Api/dashboardService';
import { apiRequest } from 'services/Auth/authConfig';
import { groupBy } from 'utils/array';
import { generatePalette } from 'utils/color';
import { newGuidNil } from 'utils/guid';
import { sortOnNumber, sortOnString } from 'utils/sorting';
import { IWidgetConfigRendererProps } from '../WidgetConfigRenderer';
import { IWidgetRendererProps } from '../WidgetRenderer';
import {
  getConfiguredTenantIds,
  getTenantWidgetTenantById,
  getTenantWidgetTenantId,
  getTenantWidgetTenantName,
  sortTenantWidgetTenantsByName,
  TenantWidgetTenant,
  WithTenantId,
} from './tenantWidgetData';
import { useTenantDrillDown } from './useTenantDrillDown';

enum WidgetTenantRiskOwnerConfigViewMode {
  User = 0,
  OrgUnit = 1,
}

export class WidgetTenantRiskOwnerConfig extends WidgetTenantConfig {
  viewMode: WidgetTenantRiskOwnerConfigViewMode;

  allowSwitchViewMode: boolean;

  constructor() {
    super();
    this.viewMode = WidgetTenantRiskOwnerConfigViewMode.User;
    this.allowSwitchViewMode = true;
  }

  load(raw: string | undefined) {
    if (raw) {
      try {
        const newRawConfig = JSON.parse(raw);
        this.loadTenant(newRawConfig);
        this.viewMode = newRawConfig.viewMode;
        this.allowSwitchViewMode = newRawConfig.allowSwitchViewMode ?? true;
      } catch {
        //ignore
      }
    }
  }

  clone(): WidgetTenantRiskOwnerConfig {
    const newConfig = this.cloneTenant() as WidgetTenantRiskOwnerConfig;
    newConfig.viewMode = this.viewMode;
    newConfig.allowSwitchViewMode = this.allowSwitchViewMode;

    return newConfig;
  }
}

interface IWidgetTenantRiskOwnerProps extends IWidgetRendererProps {}

const normalizeKey = (s: string | undefined): string => (s ?? '').trim().toLowerCase();

const countUpper = (s: string) => (s.match(/[A-Z]/g) ?? []).length;
const pickBetterCasing = (a: string, b: string): string => {
  const au = countUpper(a);
  const bu = countUpper(b);
  if (au !== bu) return au > bu ? a : b;
  if (a.length !== b.length) return a.length < b.length ? a : b;

  return sortOnString(a, b) <= 0 ? a : b;
};

const sumRisk = (rows: RiskOwnerDTO[]) => rows.reduce((acc, curr) => acc + (curr.riskCount ?? 0), 0);

const WidgetTenantRiskOwner = (props: IWidgetTenantRiskOwnerProps) => {
  const { t } = useTranslation(['translation', 'widgets']);
  const appContext = useContext(AppContext);
  const [data, setData] = useState<RiskOwnerDTO[]>([]);
  const [apiData, setApiData] = useState<RiskOwnerDTO[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [colorGradient, setColorGradient] = useState<string[]>([]);
  const [tenants, setTenants] = useState<TenantWidgetTenant[]>([]);
  const [tenantsToShow, setTenantsToShow] = useState<TenantWidgetTenant[]>([]);
  const [config, setConfig] = useState<WidgetTenantRiskOwnerConfig | undefined>(undefined);
  const [viewMode, setViewMode] = useState<WidgetTenantRiskOwnerConfigViewMode>(
    WidgetTenantRiskOwnerConfigViewMode.User,
  );

  useEffect(() => {
    const loadData = async () => {
      try {
        if (isLoading) return;
        setIsLoading(true);

        await appContext.globalDataCache.tags.getItems();
        const config = loadConfig();
        setConfig(config);
        setViewMode(config.viewMode);

        const tenants: TenantWidgetTenant[] = isPartnerWidgetType(props.widget.widgetType)
          ? props.sharedData?.partnerCustomers ?? []
          : props.sharedData?.userOrgUnits ?? [];
        setTenants(tenants);
        const tenantIds = getConfiguredTenantIds(config.tenants, tenants);
        if (tenantIds.length === 0) {
          setApiData([]);
          setData([]);
          setTenantsToShow([]);

          return;
        }

        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const apiData = await apiGetWidgetDataTenantRiskOwner(tenantIds, accessToken, props.abortSignal);

        setApiData(apiData);

        const tenantsToShow = tenants.filter((tenant) =>
          apiData.some((s) => s.tenantId === getTenantWidgetTenantId(tenant)),
        );
        sortTenantWidgetTenantsByName(tenantsToShow);
        setTenantsToShow(tenantsToShow);

        calcData(apiData, config.viewMode, tenantsToShow);
      } catch (err) {
        appContext.setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget, props.sharedData?.partnerCustomers, props.sharedData?.userOrgUnits]);

  useEffect(() => {
    if (tenantsToShow.length > 0) {
      setColorGradient(generatePalette(tenantsToShow.length));
    }
  }, [tenantsToShow]);

  const calcData = (
    apiData: RiskOwnerDTO[],
    viewMode: WidgetTenantRiskOwnerConfigViewMode,
    tenantsToShow: TenantWidgetTenant[],
  ) => {
    // normalize name field
    for (const item of apiData) {
      item.name = item.ownerName ?? item.ownerRoleName ?? '';
    }

    // Canonical casing for role names across ALL tenants (dedupe Ciso vs CISO etc.)
    const roleCanonicalByKey = new Map<string, string>();
    for (const row of apiData) {
      if (!row.ownerRoleId) continue;
      const key = normalizeKey(row.name);
      if (!key) continue;

      const candidate = (row.name ?? '').trim();
      const existing = roleCanonicalByKey.get(key);
      roleCanonicalByKey.set(key, existing ? pickBetterCasing(existing, candidate) : candidate);
    }

    const getCanonicalRoleName = (name: string | undefined) => {
      const key = normalizeKey(name);

      return roleCanonicalByKey.get(key) ?? name ?? '';
    };

    const riskOwnersToDisplay: RiskOwnerDTO[] = [];

    if (viewMode === WidgetTenantRiskOwnerConfigViewMode.OrgUnit) {
      //
      // Org unit view: x-axis = org units; stacks = users/roles
      //
      const sortedTenants = sortTenantWidgetTenantsByName([...tenantsToShow]);
      const rowsByTenant = groupBy(apiData, (r) => r.tenantId);

      for (const tenant of sortedTenants) {
        const tenantId = getTenantWidgetTenantId(tenant);
        const tenantName = getTenantWidgetTenantName(tenant);
        const tenantRows = rowsByTenant.get(tenantId) ?? [];
        if (tenantRows.length === 0) continue;

        const details: RiskOwnerDTO[] = [];

        // user owners (per tenant)
        const byOwnerId = groupBy(
          tenantRows.filter((r) => r.ownerId !== undefined),
          (r) => r.ownerId as string,
        );

        for (const [ownerId, ownerData] of byOwnerId) {
          details.push({
            tenantId,
            name: ownerData[0]?.name,
            ownerId,
            ownerRoleId: undefined,
            riskCount: sumRisk(ownerData),
            details: ownerData,
          });
        }

        // role owners (per tenant), grouped by normalized key, displayed with canonical casing across tenants
        const byRoleKey = groupBy(
          tenantRows.filter((r) => r.ownerRoleId !== undefined && normalizeKey(r.name).length > 0),
          (r) => normalizeKey(r.name),
        );

        for (const [, roleData] of byRoleKey) {
          details.push({
            tenantId,
            name: getCanonicalRoleName(roleData[0]?.name),
            ownerId: undefined,
            ownerRoleId: roleData[0]?.ownerRoleId,
            riskCount: sumRisk(roleData),
            details: roleData,
          });
        }

        // unassigned within this tenant
        const unassigned = tenantRows.filter((s) => !s.ownerId && !s.ownerRoleId);
        if (unassigned.length > 0) {
          details.push({
            tenantId,
            name: t('translation:General.OwnerPicker.NoOwner'),
            ownerId: undefined,
            ownerRoleId: undefined,
            riskCount: sumRisk(unassigned),
            details: unassigned,
          });
        }

        // sort stacks within org unit: risk desc, then name asc
        details.sort((a, b) => {
          const n = -1 * sortOnNumber(a.riskCount, b.riskCount);
          if (n !== 0) return n;

          return sortOnString(a.name, b.name);
        });

        riskOwnersToDisplay.push({
          name: tenantName,
          tenantId,
          riskCount: sumRisk(details),
          details,
        });
      }

      setData(riskOwnersToDisplay);
    } else {
      //
      // User view: x-axis = users/roles; stacks = tenants
      //
      const byOwnerId = groupBy(
        apiData.filter((r) => r.ownerId !== undefined),
        (r) => r.ownerId as string,
      );
      for (const [, ownerData] of byOwnerId) {
        riskOwnersToDisplay.push({
          name: ownerData[0]?.name,
          riskCount: sumRisk(ownerData),
          details: ownerData,
        });
      }

      const byRoleKey = groupBy(
        apiData.filter((r) => r.ownerRoleId !== undefined && normalizeKey(r.name).length > 0),
        (r) => normalizeKey(r.name),
      );
      for (const [, roleData] of byRoleKey) {
        riskOwnersToDisplay.push({
          name: getCanonicalRoleName(roleData[0]?.name),
          riskCount: sumRisk(roleData),
          details: roleData,
        });
      }

      riskOwnersToDisplay.sort((a, b) => {
        const n = -1 * sortOnNumber(a.riskCount, b.riskCount);
        if (n !== 0) return n;

        return sortOnString(a.name, b.name);
      });

      const unassigned = apiData.filter((s) => !s.ownerId && !s.ownerRoleId);
      if (unassigned.length > 0) {
        riskOwnersToDisplay.push({
          name: t('translation:General.OwnerPicker.NoOwner'),
          riskCount: sumRisk(unassigned),
          details: unassigned,
        });
      }

      setData(riskOwnersToDisplay);
    }
  };

  const loadConfig = (): WidgetTenantRiskOwnerConfig => {
    const newConfig = new WidgetTenantRiskOwnerConfig();
    newConfig.load(props.widget.widgetConfig);

    return newConfig;
  };

  const getTenantColor = (tenantId: string | undefined): string => {
    const idx = tenantsToShow.findIndex((tenant) => getTenantWidgetTenantId(tenant) === tenantId);
    if (idx === -1) return colorGradient[0];

    return colorGradient[idx];
  };

  const tenantNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const tenant of tenantsToShow) m.set(getTenantWidgetTenantId(tenant), getTenantWidgetTenantName(tenant));

    return m;
  }, [tenantsToShow]);

  const getData = (): IVerticalStackedChartProps[] => {
    if (data.length === 0 || tenantsToShow.length === 0) return [];

    if (viewMode === WidgetTenantRiskOwnerConfigViewMode.OrgUnit) {
      // x-axis = org unit (tenant), stacks = owners

      const ownerLegends = [
        ...new Set(
          data.flatMap((ou) => (ou.details ?? []).map((d) => (d.name ?? '').trim())).filter((n) => n.length > 0),
        ),
      ].sort((a, b) => sortOnString(a, b));

      const ownerPalette = generatePalette(Math.max(ownerLegends.length, 1));
      const ownerIndexByKey = new Map<string, number>();
      ownerLegends.forEach((n, i) => ownerIndexByKey.set(normalizeKey(n), i));

      const getOwnerColor = (ownerName: string) => ownerPalette[ownerIndexByKey.get(normalizeKey(ownerName)) ?? 0];

      return data.map((orgUnit) => {
        const detailsSorted = [...(orgUnit.details ?? [])].sort((a, b) => sortOnString(a.name, b.name));

        //every stack of this bar belongs to the tenant on the x axis
        const chartData: WithTenantId<IVSChartDataPoint>[] = detailsSorted.map((d) => ({
          legend: d.name ?? '',
          data: d.riskCount ?? 0,
          color: getOwnerColor(d.name ?? ''),
          xAxisCalloutData: orgUnit.name ?? '',
          tenantId: orgUnit.tenantId ?? '',
        }));

        return { chartData, xAxisPoint: orgUnit.name ?? '', tenantId: orgUnit.tenantId ?? '' };
      });
    } else {
      // User mode: x-axis = users/roles; stacks = tenants
      return data.map((s) => {
        //here the x axis is the owner and each stack is a different tenant, so the tenant travels on the point
        const chartData: WithTenantId<IVSChartDataPoint>[] = (s.details ?? []).map((d) => ({
          legend: tenantNameById.get(d.tenantId ?? '') ?? '',
          data: d.riskCount ?? 0,
          color: getTenantColor(d.tenantId),
          xAxisCalloutData: d.name ?? '',
          tenantId: d.tenantId ?? '',
        }));

        return { chartData, xAxisPoint: s.name ?? '' };
      });
    }
  };

  const getOwnerForName = (tenantId: string | undefined, name: string | undefined): string | undefined => {
    if (!tenantId) return undefined;

    const noOwnerLabel = t('translation:General.OwnerPicker.NoOwner');
    if (!name || normalizeKey(name) === normalizeKey(noOwnerLabel)) return newGuidNil();

    const details = data.flatMap((d) => d.details?.filter((detail) => detail.tenantId === tenantId) ?? []);
    const owner = details.find((d) => normalizeKey(d?.name) === normalizeKey(name));
    if (!owner) return undefined;

    return owner.ownerId ?? owner.ownerRoleId;
  };

  const drillDown = useTenantDrillDown();

  const onClick = (dataPoint: IVSChartDataPoint) => {
    let url = '/risks/overview';
    let tenant: TenantWidgetTenant | undefined = undefined;
    let filter: string | undefined = undefined;

    //The tenant comes from the point itself. Which axis carries the OWNER name still depends on the view mode:
    //in org unit mode the owner is the stack, in user mode it is the x axis.
    const pointTenant = getTenantWidgetTenantById(tenants, (dataPoint as WithTenantId<IVSChartDataPoint>).tenantId);

    switch (config?.viewMode) {
      case WidgetTenantRiskOwnerConfigViewMode.OrgUnit:
        tenant = pointTenant;
        filter = getOwnerForName(getTenantWidgetTenantId(tenant), dataPoint.legend);
        break;
      case WidgetTenantRiskOwnerConfigViewMode.User:
        tenant = pointTenant;
        filter = getOwnerForName(getTenantWidgetTenantId(tenant), dataPoint.xAxisCalloutData);
        break;
      default:
        break;
    }

    if (!tenant) return;
    if (filter) url += `?owner=${filter}`;
    drillDown(tenant, url, isPartnerWidgetType(props.widget.widgetType));
  };

  //
  // Main render
  //
  if (!data || isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  if (!isLoading && (data.length === 0 || tenantsToShow.length === 0)) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPText>{t('translation:General.Notifications.NoItemsFilter')}</IPText>
      </IPStack>
    );
  }

  const shouldRotateXAxisLabels = data.length > 10 && props.widget.widgetSize !== WidgetSizes.Large;

  return (
    <IPStack verticalFill>
      {config?.allowSwitchViewMode && (
        <IPStack.Item>
          <IPActionButton
            iconProps={viewIcon}
            styles={{ root: { height: 20 } }}
            text={`${t('widgets:TenantRiskOwnerOrgUnit.ViewModeLabel')}: ${
              viewMode === WidgetTenantRiskOwnerConfigViewMode.User
                ? t('widgets:TenantRiskOwnerOrgUnit.Config.ViewMode.User')
                : t('widgets:TenantRiskOwnerOrgUnit.Config.ViewMode.OrgUnit')
            }`}
            onClick={() => {
              const newViewMode =
                viewMode === WidgetTenantRiskOwnerConfigViewMode.User
                  ? WidgetTenantRiskOwnerConfigViewMode.OrgUnit
                  : WidgetTenantRiskOwnerConfigViewMode.User;
              setViewMode(newViewMode);
              calcData(apiData, newViewMode, tenantsToShow);
            }}
          />
        </IPStack.Item>
      )}
      <IPStack.Item grow>
        <VerticalStackedBarChart
          data={getData()}
          height={250}
          width={WidgetWidth[props.widget.widgetSize] - 80}
          wrapXAxisLables={!shouldRotateXAxisLabels}
          rotateXAxisLables={shouldRotateXAxisLabels}
          barWidth={'auto'}
          legendProps={{
            centerLegends: true,
          }}
          onBarClick={(ev, data) => onClick(data as IVSChartDataPoint)}
        />
      </IPStack.Item>
    </IPStack>
  );
};

export default WidgetTenantRiskOwner;

//
// Config
//
interface IWidgetConfigTenantRiskOwnerProps extends IWidgetConfigRendererProps {}

export const WidgetConfigTenantRiskOwner = (props: IWidgetConfigTenantRiskOwnerProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation', 'widgets']);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedTenants, setSelectedTenants] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<WidgetTenantRiskOwnerConfigViewMode>(
    WidgetTenantRiskOwnerConfigViewMode.User,
  );
  const [allowSwitchViewMode, setAllowSwitchViewMode] = useState<boolean>(true);
  const [currentWidgetId, setCurrentWidgetId] = useState<number>(-1);

  useEffect(() => {
    if (currentWidgetId === -1 || currentWidgetId !== props.widget.widgetId) {
      setCurrentWidgetId(props.widget.widgetId);
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget]);

  const loadConfig = (): WidgetTenantRiskOwnerConfig => {
    let newRawConfig = new WidgetTenantRiskOwnerConfig();
    if (props.widget.widgetConfig) {
      newRawConfig = JSON.parse(props.widget.widgetConfig);
    }

    return newRawConfig;
  };

  const updateTenants = (items: PickerTenant[]) => {
    const tenants = items.map((i) => i.tenantId);
    setSelectedTenants(tenants);
    const config = getConfig(tenants, viewMode, allowSwitchViewMode);
    props.onUpdateConfig(JSON.stringify(config), true);
  };

  const updateViewMode = (viewMode: WidgetTenantRiskOwnerConfigViewMode) => {
    setViewMode(viewMode);
    const config = getConfig(selectedTenants, viewMode, allowSwitchViewMode);
    props.onUpdateConfig(JSON.stringify(config), true);
  };

  const updateAllowSwitchViewMode = (allow: boolean) => {
    setAllowSwitchViewMode(allow);
    const config = getConfig(selectedTenants, viewMode, allow);
    props.onUpdateConfig(JSON.stringify(config), true);
  };

  const loadData = async () => {
    try {
      if (isLoading) return;
      setIsLoading(true);

      const config = loadConfig();
      setSelectedTenants(config.tenants);
      setViewMode(config.viewMode);
      setAllowSwitchViewMode(config.allowSwitchViewMode);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const getConfig = (
    items: string[],
    viewMode: WidgetTenantRiskOwnerConfigViewMode,
    allow: boolean,
  ): WidgetTenantRiskOwnerConfig => {
    const config = new WidgetTenantRiskOwnerConfig();
    config.tenants = items;
    config.viewMode = viewMode;
    config.allowSwitchViewMode = allow;

    return config;
  };

  const getViewModeOptions = (): IChoiceGroupOption[] => {
    return [
      {
        key: WidgetTenantRiskOwnerConfigViewMode.User.toString(),
        text: t('widgets:TenantRiskOwnerOrgUnit.Config.ViewMode.User'),
      },
      {
        key: WidgetTenantRiskOwnerConfigViewMode.OrgUnit.toString(),
        text: t('widgets:TenantRiskOwnerOrgUnit.Config.ViewMode.OrgUnit'),
      },
    ];
  };

  //
  // Main render
  //
  if (isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  return (
    <IPStack verticalFill tokens={globalStackTokensGapMedium}>
      <IPStack.Item>
        <TenantPicker
          mode={isPartnerWidgetType(props.widget.widgetType) ? TenantPickerMode.Partner : TenantPickerMode.User}
          onSelect={updateTenants}
          selectedTenants={selectedTenants}
          emptySelectionMeansAll
        />
      </IPStack.Item>
      <IPStack.Item>
        <IPChoiceGroup
          label={t('widgets:TenantRiskOwnerOrgUnit.Config.ViewMode.Label')}
          selectedKey={viewMode.toString()}
          options={getViewModeOptions()}
          onChange={(ev, option) => updateViewMode(Number(option?.key))}
        />
      </IPStack.Item>
      <IPStack.Item>
        <IPCheckbox
          checked={allowSwitchViewMode}
          label={t('widgets:TenantRiskOwnerOrgUnit.Config.ViewMode.AllowSwitchViewMode')}
          onChange={(ev, checked) => updateAllowSwitchViewMode(checked ?? false)}
        />
      </IPStack.Item>
    </IPStack>
  );
};
